import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectApi, mfaApi } from '../hooks/useApi';
import { useTheme } from '../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import { C, Btn, Card, Empty, ThemeToggle, Icon } from '../components/UI';

const ACCENTS = ['var(--blue)', 'var(--purple)', 'var(--green)', 'var(--amber)', 'var(--orange)'];
function cardAccent(id) {
  const h = (id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return ACCENTS[h % ACCENTS.length];
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('ebios_user') || '{}');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showNew, setShowNew]   = useState(false);
  const [form, setForm]         = useState({ name:'', org:'', date:'', scope:'', context:'' });
  const [saving, setSaving]     = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(null);

  useEffect(() => {
    load();
    mfaApi.status().then(d => setMfaEnabled(d.mfaEnabled)).catch(() => {});
  }, []);

  async function load() {
    try { setProjects(await projectApi.list()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function create(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const p = await projectApi.create({ ...form, date: form.date || new Date().toISOString().split('T')[0], er:[], socle:[], sr:[], ss:[], so:[], mesures:[] });
      navigate(`/project/${p._id}`);
    } catch { alert(t('common.error')); }
    finally { setSaving(false); }
  }

  function logout() { localStorage.clear(); window.location.href = '/login'; }

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', color:'var(--text)', fontFamily:'var(--font-sans)' }}>

      {/* TOPBAR */}
      <header style={S.topbar}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={S.logoMark}>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, letterSpacing:-0.5, lineHeight:1.1 }}>{t('app.name')}</div>
            <div style={{ fontSize:9.5, color:'var(--text3)', letterSpacing:1.8, textTransform:'uppercase', marginTop:1 }}>{t('app.tagline')}</div>
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {mfaEnabled === false && (
            <a href="/mfa/setup" style={S.mfaWarn}>
              <span>⚠</span> {t('mfa.activate_prompt')}
            </a>
          )}
          {mfaEnabled === true && (
            <span style={S.mfaOk}><span>✓</span> {t('mfa.active')}</span>
          )}
          <span style={S.userChip}>{user.name}</span>
          <LanguageSelector />
          <ThemeToggle theme={theme} onToggle={toggle} />
          <Btn variant="secondary" onClick={logout} style={{ height:32, padding:'0 12px', fontSize:12 }}>{t('nav.logout')}</Btn>
        </div>
      </header>

      {/* MAIN */}
      <main style={{ maxWidth:1120, margin:'0 auto', padding:'36px 24px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:28 }}>
          <div>
            <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:700, letterSpacing:-0.5 }}>{t('dashboard.title')}</h1>
            <p style={{ fontSize:13, color:'var(--text3)', marginTop:4 }}>
              {t('dashboard.projectCount_other', { count: projects.length })}
            </p>
          </div>
          <Btn onClick={() => setShowNew(true)}>
            <Icon.Plus /> {t('dashboard.newProject')}
          </Btn>
        </div>

        {/* FORMULAIRE NOUVEAU PROJET */}
        {showNew && (
          <Card title={t('project.newTitle')} style={{ marginBottom:24, borderColor:'var(--blue-border)' }}>
            <div style={{ padding:'20px' }}>
              <form onSubmit={create}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:4 }}>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={S.lbl}>{t('project.name')} <span style={{ color:'var(--blue)' }}>*</span></label>
                    <input style={S.inp} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder={t('project.namePlaceholder')} required autoFocus />
                  </div>
                  <div>
                    <label style={S.lbl}>{t('project.org')}</label>
                    <input style={S.inp} value={form.org} onChange={e=>setForm({...form,org:e.target.value})} />
                  </div>
                  <div>
                    <label style={S.lbl}>{t('project.date')}</label>
                    <input style={S.inp} type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})} />
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={S.lbl}>{t('project.scope')}</label>
                    <textarea style={{ ...S.inp, resize:'vertical', minHeight:68 }} value={form.scope} onChange={e=>setForm({...form,scope:e.target.value})} rows={3} />
                  </div>
                  <div style={{ gridColumn:'1/-1' }}>
                    <label style={S.lbl}>{t('project.context')}</label>
                    <textarea style={{ ...S.inp, resize:'vertical', minHeight:68 }} value={form.context} onChange={e=>setForm({...form,context:e.target.value})} rows={3} />
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:8, paddingTop:16, borderTop:'1px solid var(--border)', marginTop:12 }}>
                  <Btn variant="secondary" onClick={() => setShowNew(false)}>{t('common.cancel')}</Btn>
                  <Btn type="submit" disabled={saving}>{saving ? t('dashboard.creating') : t('dashboard.create')}</Btn>
                </div>
              </form>
            </div>
          </Card>
        )}

        {/* LISTE DES PROJETS */}
        {loading ? (
          <div style={{ textAlign:'center', padding:80, color:'var(--text3)' }}>{t('common.loading')}</div>
        ) : projects.length === 0 && !showNew ? (
          <Empty icon="📋" text={t('dashboard.noProjects')} sub={t('dashboard.noProjectsSub')} />
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(330px,1fr))', gap:16 }}>
            {projects.map((p, i) => {
              const accent = cardAccent(p._id);
              const initials = (p.org || p.name || '?').slice(0, 2).toUpperCase();
              return (
                <ProjectCard
                  key={p._id}
                  p={p}
                  accent={accent}
                  initials={initials}
                  index={i}
                  onOpen={() => navigate(`/project/${p._id}`)}
                  onDelete={async () => {
                    if (window.confirm(t('dashboard.confirmDelete', { name: p.name }))) {
                      await projectApi.delete(p._id);
                      load();
                    }
                  }}
                  t={t}
                />
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function ProjectCard({ p, accent, initials, index, onOpen, onDelete, t }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'var(--bg-subtle)',
        border: `1px solid ${hov ? accent : 'var(--border)'}`,
        borderRadius: 8,
        padding: 18,
        transition: 'all .15s ease',
        transform: hov ? 'translateY(-1px)' : 'translateY(0)',
        boxShadow: hov ? 'var(--shadow-md)' : 'none',
        animation: `fadeUp 0.3s ease ${index * 0.04}s both`,
      }}>
      {/* Accent strip */}
      <div style={{ height: 3, background: accent, margin: '-18px -18px 16px', borderRadius: '7px 7px 0 0' }} />

      {/* Header row */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:11, marginBottom:11 }}>
        <div style={{ width:36, height:36, borderRadius:8, background:`color-mix(in srgb, ${accent} 10%, transparent)`, border:`1px solid color-mix(in srgb, ${accent} 25%, transparent)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontFamily:'var(--font-mono)', fontSize:12, fontWeight:700, color:accent }}>
          {initials}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:14, fontWeight:600, color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontFamily:'var(--font-display)', letterSpacing:-0.2 }}>{p.name}</div>
          {p.org && <div style={{ fontSize:11.5, color:'var(--text3)', marginTop:1 }}>{p.org}</div>}
        </div>
        {p.isShared && (
          <span style={{ fontSize:10.5, background:'var(--blue-bg)', color:'var(--blue)', border:'1px solid var(--blue-border)', padding:'2px 8px', borderRadius:4, flexShrink:0, fontWeight:500 }}>
            {t('dashboard.shared')}
          </span>
        )}
      </div>

      {/* Scope excerpt */}
      {p.scope && (
        <div style={{ fontSize:12.5, color:'var(--text2)', lineHeight:1.55, marginBottom:11, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' }}>
          {p.scope}
        </div>
      )}

      {/* Date */}
      <div style={{ fontSize:11.5, color:'var(--text3)', marginBottom:14 }}>
        {t('dashboard.modified', { date: new Date(p.updatedAt).toLocaleDateString() })}
      </div>

      {/* Actions */}
      <div style={{ display:'flex', gap:7, paddingTop:12, borderTop:'1px solid var(--border)' }}>
        <Btn onClick={onOpen} style={{ flex:1, justifyContent:'center', height:32, padding:'0 12px', fontSize:12.5 }}>{t('dashboard.open')}</Btn>
        <Btn variant="danger" onClick={onDelete} style={{ height:32, padding:'0 12px', fontSize:12.5 }}>{t('dashboard.delete')}</Btn>
      </div>
    </div>
  );
}

const S = {
  topbar:   { padding:'0 24px', height:54, borderBottom:'1px solid var(--border)', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:50 },
  logoMark: { width:30, height:30, background:'var(--blue)', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:'0 0 0 3px var(--blue-bg)' },
  userChip: { fontSize:12, color:'var(--text2)', background:'var(--bg-muted)', border:'1px solid var(--border)', padding:'4px 10px', borderRadius:20, fontWeight:500 },
  mfaWarn:  { fontSize:12, background:'var(--amber-bg)', color:'var(--amber)', border:'1px solid var(--amber-border)', padding:'5px 12px', borderRadius:6, textDecoration:'none', fontWeight:500, display:'flex', alignItems:'center', gap:5 },
  mfaOk:    { fontSize:12, color:'var(--green)', background:'var(--green-bg)', border:'1px solid var(--green-border)', padding:'5px 12px', borderRadius:6, display:'flex', alignItems:'center', gap:5, fontWeight:500 },
  lbl:      { display:'block', fontSize:11, fontWeight:600, color:'var(--text3)', textTransform:'uppercase', letterSpacing:0.8, marginBottom:6 },
  inp:      { background:'var(--bg-muted)', border:'1px solid var(--border)', color:'var(--text)', padding:'8px 12px', borderRadius:6, fontSize:13, outline:'none', width:'100%', boxSizing:'border-box', transition:'border-color .12s' },
};
